import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMsal } from '@azure/msal-react';
import {
  listOneClickFunctions,
  createOneClickFunction,
  updateOneClickFunction,
  deleteOneClickFunction,
} from '../../helper/llmApi';
import OneClickForm from './OneClickForm';
import PageHeader from '../common/PageHeader';

export default function OneClickSettingsView() {
  const { instance } = useMsal();
  const [functions, setFunctions] = useState({ global: [], user: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listOneClickFunctions(instance);
      setFunctions({
        global: data?.global || [],
        user: data?.user || [],
      });
    } catch (e) {
      setError(e.message || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (fn) => {
    setEditing({
      id: fn.id,
      name: fn.name,
      description: fn.description,
      icon: fn.icon,
      prompt_template: fn.prompt_template,
    });
    setFormOpen(true);
  };

  const handleSubmit = async (payload) => {
    if (editing?.id) {
      await updateOneClickFunction(instance, editing.id, payload);
    } else {
      await createOneClickFunction(instance, payload);
    }
    await reload();
  };

  const handleDelete = async (fn) => {
    if (!window.confirm(`Funktion "${fn.name}" wirklich löschen?`)) return;
    try {
      await deleteOneClickFunction(instance, fn.id);
      await reload();
    } catch (e) {
      setError(e.message || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <Box sx={{ maxWidth: 840, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <PageHeader
        overline="Einstellungen"
        title="1-Click KI-Funktionen"
        subtitle="Lege hilfreiche KI-Funktionen für deine Transkripte an und verwalte sie hier."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Neue Funktion
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Bereits vorhanden
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Diese Funktionen stehen dir direkt zur Verfügung.
              </Typography>
            </Box>
            <Divider />
            <List dense>
              {functions.global.length === 0 && (
                <ListItem><ListItemText secondary="Zurzeit sind keine vordefinierten Funktionen verfügbar." /></ListItem>
              )}
              {functions.global.map((fn) => (
                <ListItem key={fn.id}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <span style={{ fontSize: 18 }}>{fn.icon || '✨'}</span>
                        <Typography variant="body1">{fn.name}</Typography>
                      </Box>
                    }
                    secondary={fn.description}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Deine Funktionen
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Diese Funktionen hast du selbst angelegt.
              </Typography>
            </Box>
            <Divider />
            <List dense>
              {functions.user.length === 0 && (
                <ListItem>
                  <ListItemText secondary="Du hast noch keine eigene Funktion angelegt." />
                </ListItem>
              )}
              {functions.user.map((fn) => (
                <ListItem
                  key={fn.id}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Bearbeiten">
                        <IconButton edge="end" onClick={() => openEdit(fn)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton edge="end" color="error" onClick={() => handleDelete(fn)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <span style={{ fontSize: 18 }}>{fn.icon || '✨'}</span>
                        <Typography variant="body1">{fn.name}</Typography>
                      </Box>
                    }
                    secondary={fn.description}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Stack>
      )}

      <OneClickForm
        open={formOpen}
        initial={editing}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />
    </Box>
  );
}
